import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, X } from "lucide-react";
import { api, SyncJobInfo, SyncChunkInfo } from "../../lib/api";

const MONTHS_UK = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер",
                   "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];

type ChunkParsed = {
  raw: SyncChunkInfo;
  year: number;
  month: number | null;   // 1..12 для PBI, null для других
  quarter: number | null; // 1..4 для referrals/finance
  kind: string | null;    // 'fac', 'pkg' для PBI, null для остальных
};

/** Парсить period_covered у структуру для heatmap позиціонування */
function parsePeriod(p: string): Omit<ChunkParsed, "raw"> | null {
  // PBI: '2024-01-fac' / '2024-01-pkg'
  let m = p.match(/^(\d{4})-(\d{2})-(fac|pkg)$/);
  if (m) return { year: +m[1], month: +m[2], quarter: null, kind: m[3] };
  // referrals: '2024-1qrt'
  m = p.match(/^(\d{4})-(\d)qrt$/);
  if (m) return { year: +m[1], month: null, quarter: +m[2], kind: null };
  // finance: '12m-2024' / '6m-2024' / '9m-2024' / '1K-2024'
  m = p.match(/^(\d+m|\dK)-(\d{4})$/i);
  if (m) {
    const code = m[1].toLowerCase();
    const q = code === "1k" ? 1 : code === "6m" ? 2 : code === "9m" ? 3 : code === "12m" ? 4 : null;
    return { year: +m[2], month: null, quarter: q, kind: code };
  }
  // payments: '2024'
  m = p.match(/^(\d{4})$/);
  if (m) return { year: +m[1], month: null, quarter: null, kind: null };
  return null;
}

export function SyncPage() {
  const qc = useQueryClient();
  const [detail, setDetail] = useState<SyncChunkInfo | null>(null);

  const anyActive = (rows?: SyncJobInfo[]) =>
    !!rows?.some(r => r.queue?.status === "pending" || r.queue?.status === "running");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-sync"],
    queryFn: () => api.get<SyncJobInfo[]>("/api/v1/admin/sync"),
    refetchInterval: (q) => (anyActive(q.state.data as SyncJobInfo[] | undefined) ? 5_000 : 30_000),
  });

  const { data: chunks } = useQuery({
    queryKey: ["admin-sync-chunks"],
    queryFn: () => api.get<SyncChunkInfo[]>("/api/v1/admin/sync/chunks?days=400"),
    refetchInterval: () => (anyActive(data) ? 5_000 : 60_000),
  });

  const trigger = useMutation({
    mutationFn: ({ source, period }: { source: string; period?: string }) => {
      const qs = period ? `?period=${encodeURIComponent(period)}` : "";
      return api.post<{ status: string; note: string }>(`/api/v1/admin/sync/${source}/trigger${qs}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-sync"] });
      qc.invalidateQueries({ queryKey: ["admin-sync-chunks"] });
      setDetail(null);
    },
  });

  // Групуємо chunks за source, у кожній групі — за роком, потім за period_covered (latest first)
  const chunksBySource = useMemo(() => {
    const out: Record<string, ChunkParsed[]> = {};
    for (const ch of chunks || []) {
      const parsed = parsePeriod(ch.period_covered);
      if (!parsed) continue;
      (out[ch.source] ||= []).push({ raw: ch, ...parsed });
    }
    return out;
  }, [chunks]);

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;

  return (
    <div style={{ padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, color: "#1a365d", margin: 0 }}>Контроль синхронізації</h1>
        <button onClick={() => {
          qc.invalidateQueries({ queryKey: ["admin-sync"] });
          qc.invalidateQueries({ queryKey: ["admin-sync-chunks"] });
        }} style={btnSecondary}>
          <RefreshCw size={14} /> Оновити
        </button>
      </div>
      <p style={{ color: "#718096", fontSize: 13, marginBottom: 16 }}>
        Sync-jobs виконуються worker-контейнером за розкладом. «Запустити» → запит у черзі,
        воркер підхопить протягом 15 сек. Heatmap внизу показує per-period покриття: клікни
        на клітинку → деталі + повторний sync лише цього періоду.
      </p>

      <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden", marginBottom: 24 }}>
        <thead>
          <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
            <th style={th}>Джерело</th>
            <th style={th}>Розклад</th>
            <th style={th}>Останній запуск</th>
            <th style={th}>Статус</th>
            <th style={{ ...th, textAlign: "right" }}>Insert</th>
            <th style={{ ...th, textAlign: "right" }}>Update</th>
            <th style={{ ...th, textAlign: "right" }}>Тривалість</th>
            <th style={th}>Дії</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((j) => (
            <tr key={j.source} style={{ borderTop: "1px solid #e2e8f0" }}>
              <td style={td}>
                <strong>{j.name}</strong>
                <div style={{ fontSize: 10.5, color: "#a0aec0", fontFamily: "ui-monospace, monospace" }}>{j.source}</div>
              </td>
              <td style={{ ...td, color: "#4a5568", fontSize: 12 }}>{j.schedule}</td>
              <td style={{ ...td, color: "#718096", fontSize: 12 }}>
                {j.last_run?.finished_at
                  ? new Date(j.last_run.finished_at).toLocaleString("uk-UA")
                  : "—"}
                {j.last_run?.triggered_by && (
                  <div style={{ fontSize: 10.5, color: "#a0aec0" }}>by {j.last_run.triggered_by}</div>
                )}
              </td>
              <td style={td}>
                {j.last_run ? (
                  <span style={{
                    color: j.last_run.status === "success" ? "#38a169"
                         : j.last_run.status === "failed" ? "#e53e3e" : "#d69e2e",
                    fontWeight: 600,
                  }}>{j.last_run.status}</span>
                ) : (
                  <span style={{ color: "#a0aec0" }}>не запускався</span>
                )}
                {j.last_run?.error_message && (
                  <div style={{ fontSize: 10.5, color: "#e53e3e", maxWidth: 220 }} title={j.last_run.error_message}>
                    {j.last_run.error_message.slice(0, 60)}…
                  </div>
                )}
              </td>
              <td style={{ ...td, textAlign: "right" }}>{j.last_run?.rows_inserted?.toLocaleString("uk-UA") ?? "—"}</td>
              <td style={{ ...td, textAlign: "right" }}>{j.last_run?.rows_updated?.toLocaleString("uk-UA") ?? "—"}</td>
              <td style={{ ...td, textAlign: "right", color: "#718096" }}>
                {j.last_run?.duration_seconds != null ? `${Math.round(j.last_run.duration_seconds)}с` : "—"}
              </td>
              <td style={td}>
                {j.queue?.status === "pending" ? (
                  <span style={badgeQueued}>у черзі…</span>
                ) : j.queue?.status === "running" ? (
                  <span style={badgeRunning}>виконується…</span>
                ) : (
                  <button onClick={() => trigger.mutate({ source: j.source })}
                          disabled={trigger.isPending} style={btnSecondary}>
                    <Play size={12} /> Запустити
                  </button>
                )}
                {j.queue?.status === "failed" && j.queue.error_message && (
                  <div style={{ fontSize: 10.5, color: "#e53e3e", marginTop: 4, maxWidth: 220 }}
                       title={j.queue.error_message}>
                    queue err: {j.queue.error_message.slice(0, 50)}…
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Per-source heatmaps */}
      <h2 style={{ fontSize: 16, color: "#1a365d", margin: "8px 0 12px" }}>Покриття по періодах</h2>
      {data?.map((j) => {
        const sourceChunks = chunksBySource[j.source] || [];
        if (sourceChunks.length === 0) return null;
        return (
          <SourceHeatmap
            key={j.source}
            source={j.source}
            displayName={j.name}
            chunks={sourceChunks}
            onClick={(ch) => setDetail(ch)}
          />
        );
      })}

      {/* Drill-down modal */}
      {detail && (
        <ChunkDetailModal
          chunk={detail}
          onClose={() => setDetail(null)}
          onResync={(source, period) => trigger.mutate({ source, period })}
          isResyncing={trigger.isPending}
        />
      )}
    </div>
  );
}

function SourceHeatmap({
  source, displayName, chunks, onClick,
}: {
  source: string;
  displayName: string;
  chunks: ChunkParsed[];
  onClick: (c: SyncChunkInfo) => void;
}) {
  const years = Array.from(new Set(chunks.map(c => c.year))).sort();
  // Для кожного року тримаємо найсвіжіший chunk per (year, month/quarter, kind)
  const latest = useMemo(() => {
    const map: Record<string, ChunkParsed> = {};
    for (const c of chunks) {
      const key = `${c.year}-${c.month ?? "Q" + c.quarter}-${c.kind ?? ""}`;
      const existing = map[key];
      if (!existing) { map[key] = c; continue; }
      const a = existing.raw.started_at || "";
      const b = c.raw.started_at || "";
      if (b > a) map[key] = c;
    }
    return map;
  }, [chunks]);

  // Тип шкали: monthly (PBI) — 12 колонок, quarterly (referrals/finance) — 4 квадрати, yearly (payments) — 1 квадрат
  const sample = chunks[0];
  const isMonthly = sample.month !== null;
  const isQuarterly = !isMonthly && sample.quarter !== null;
  const kinds = isMonthly
    ? Array.from(new Set(chunks.map(c => c.kind).filter(Boolean))) as string[]
    : sample.kind ? [sample.kind] : [""];

  return (
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 6, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div>
          <strong style={{ color: "#2c5282" }}>{displayName}</strong>
          <span style={{ marginLeft: 8, fontSize: 11, color: "#a0aec0", fontFamily: "ui-monospace,monospace" }}>{source}</span>
        </div>
        <div style={{ fontSize: 11, color: "#718096" }}>
          {chunks.length} chunks за {years.length} р. · клітинка = клік для деталей
        </div>
      </div>

      {kinds.map(kind => (
        <div key={kind} style={{ marginBottom: kinds.length > 1 ? 10 : 0 }}>
          {kinds.length > 1 && (
            <div style={{ fontSize: 11, color: "#718096", marginBottom: 4 }}>
              {kind === "fac" ? "Послуги по фасилітіс" : kind === "pkg" ? "По пакетах ПМГ" : kind}
            </div>
          )}
          <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <th style={hth}></th>
                {isMonthly && MONTHS_UK.map(m => <th key={m} style={hth}>{m}</th>)}
                {isQuarterly && [1, 2, 3, 4].map(q => <th key={q} style={hth}>Q{q}</th>)}
                {!isMonthly && !isQuarterly && <th style={hth}>Рік</th>}
              </tr>
            </thead>
            <tbody>
              {years.map(yr => (
                <tr key={yr}>
                  <td style={hth}><strong>{yr}</strong></td>
                  {isMonthly && Array.from({ length: 12 }, (_, i) => i + 1).map(mo => {
                    const c = latest[`${yr}-${mo}-${kind}`];
                    return <Cell key={mo} chunk={c} onClick={onClick} />;
                  })}
                  {isQuarterly && [1, 2, 3, 4].map(q => {
                    const c = latest[`${yr}-Q${q}-${kind || ""}`];
                    return <Cell key={q} chunk={c} onClick={onClick} />;
                  })}
                  {!isMonthly && !isQuarterly && (() => {
                    const c = latest[`${yr}-Q${"null"}-`];
                    return <Cell chunk={c} onClick={onClick} />;
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Cell({ chunk, onClick }: { chunk?: ChunkParsed; onClick: (c: SyncChunkInfo) => void }) {
  if (!chunk) {
    return <td style={cellEmpty} title="Немає даних"></td>;
  }
  const status = chunk.raw.status;
  const bg = status === "success" ? "#9ae6b4"
           : status === "failed"  ? "#feb2b2"
           : status === "empty"   ? "#e2e8f0"
           : status === "partial" ? "#fef08a"
           : "#cbd5e0";
  const ins = chunk.raw.rows_inserted ?? 0;
  const upd = chunk.raw.rows_updated ?? 0;
  const total = ins + upd;
  const tooltip = `${chunk.raw.period_covered} · ${status} · ins=${ins} upd=${upd} skp=${chunk.raw.rows_skipped ?? 0}`;
  return (
    <td
      style={{ ...cellBase, background: bg }}
      title={tooltip}
      onClick={() => onClick(chunk.raw)}
    >
      {total > 0 ? Math.round(total / 1000) + "k" : ""}
    </td>
  );
}

function ChunkDetailModal({ chunk, onClose, onResync, isResyncing }: {
  chunk: SyncChunkInfo;
  onClose: () => void;
  onResync: (source: string, period: string) => void;
  isResyncing: boolean;
}) {
  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong>{chunk.source} · {chunk.period_covered}</strong>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>
        <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
          <tbody>
            <KV k="Status" v={chunk.status} />
            <KV k="Started" v={chunk.started_at ? new Date(chunk.started_at).toLocaleString("uk-UA") : "—"} />
            <KV k="Finished" v={chunk.finished_at ? new Date(chunk.finished_at).toLocaleString("uk-UA") : "—"} />
            <KV k="Тривалість" v={chunk.duration_seconds != null ? `${chunk.duration_seconds.toFixed(1)} с` : "—"} />
            <KV k="Inserted" v={(chunk.rows_inserted ?? 0).toLocaleString("uk-UA")} />
            <KV k="Updated" v={(chunk.rows_updated ?? 0).toLocaleString("uk-UA")} />
            <KV k="Skipped" v={(chunk.rows_skipped ?? 0).toLocaleString("uk-UA")} />
            <KV k="Triggered by" v={chunk.triggered_by ?? "—"} />
            {chunk.error_message && <KV k="Error" v={chunk.error_message} />}
          </tbody>
        </table>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>Закрити</button>
          <button
            onClick={() => onResync(chunk.source, chunk.period_covered)}
            disabled={isResyncing}
            style={btnPrimary}
          >
            <Play size={12} /> Re-sync this period
          </button>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <tr>
      <td style={{ padding: "4px 8px", color: "#718096", width: 120 }}>{k}</td>
      <td style={{ padding: "4px 8px", color: "#2d3748", wordBreak: "break-word" }}>{v}</td>
    </tr>
  );
}

const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600, fontSize: 12 };
const td: React.CSSProperties = { padding: "8px 12px" };
const hth: React.CSSProperties = { padding: "3px 6px", fontSize: 10.5, color: "#718096", fontWeight: 500, textAlign: "center" };
const cellBase: React.CSSProperties = {
  width: 32, height: 22, padding: 0, border: "1px solid #fff",
  textAlign: "center", fontSize: 9.5, color: "#1a365d",
  cursor: "pointer", userSelect: "none",
};
const cellEmpty: React.CSSProperties = {
  width: 32, height: 22, padding: 0, border: "1px solid #fff",
  background: "#f7fafc",
};
const btnSecondary: React.CSSProperties = { padding: "5px 10px", background: "white", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 };
const btnPrimary: React.CSSProperties = { ...btnSecondary, background: "#2c5282", color: "white", border: "1px solid #2c5282" };
const iconBtn: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", color: "#718096", padding: 4 };
const badgeQueued: React.CSSProperties = { display: "inline-block", padding: "4px 8px", background: "#fefcbf", color: "#744210", borderRadius: 4, fontSize: 11, fontWeight: 600 };
const badgeRunning: React.CSSProperties = { display: "inline-block", padding: "4px 8px", background: "#bee3f8", color: "#2a4365", borderRadius: 4, fontSize: 11, fontWeight: 600 };
const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modalBox: React.CSSProperties = {
  background: "white", borderRadius: 8, padding: 20, width: "90%", maxWidth: 600,
  maxHeight: "80vh", overflow: "auto", boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
};
